import { CreateFormData } from "@/components/create/types";
import {
  getPresetByUrlParam,
  FRONT_PRESETS,
} from "@/components/create/presets";

export interface UrlParamValues {
  prompt: string | null;
  length: string | null;
  sleeve: string | null;
  neckline: string | null;
  silhouette: string | null;
  fabric: string | null;
  preset: string | null;
  color: string | null;
}

interface AuthoritativeCreateRestoreParams {
  modifyParam: string | null;
  ticketIdParam: string | null;
  galleryDesignParam: string | null;
}

function parseGalleryDesignParam(
  galleryDesignParam: string | null
): Record<string, unknown> | null {
  if (!galleryDesignParam) return null;

  const candidates = [galleryDesignParam];
  try {
    const decoded = decodeURIComponent(galleryDesignParam);
    if (decoded !== galleryDesignParam) candidates.push(decoded);
  } catch {
    // The raw representation may still be valid JSON.
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Try the decoded representation next.
    }
  }
  return null;
}

export function getGalleryDesignTicketId(
  galleryDesignParam: string | null
): string | null {
  const parsed = parseGalleryDesignParam(galleryDesignParam);
  return typeof parsed?.id === "string" && parsed.id.trim()
    ? parsed.id.trim()
    : null;
}

function hasUsableGalleryDesignPayload(
  galleryDesignParam: string | null
): boolean {
  const parsed = parseGalleryDesignParam(galleryDesignParam);
  if (!parsed) return false;
  return [
    "id",
    "prompt",
    "image_url",
    "image_path",
    "original_image_path",
  ].some(
    key => typeof parsed[key] === "string" && parsed[key].trim().length > 0
  );
}

/**
 * Returns the explicit URL target that owns the create page, in restoration
 * priority order. Keeping the mode in the key makes same-ticket transitions
 * (for example, modification to back generation) invalidate in-flight work.
 */
export function createAuthoritativeCreateRestoreTargetKey({
  modifyParam,
  ticketIdParam,
  galleryDesignParam,
}: AuthoritativeCreateRestoreParams): string | null {
  if (modifyParam) return `modify:${modifyParam}`;
  if (ticketIdParam) return `ticket:${ticketIdParam}`;
  if (hasUsableGalleryDesignPayload(galleryDesignParam)) {
    return `gallery:${galleryDesignParam}`;
  }
  return null;
}

/**
 * Builds form data from URL parameters
 */
export function buildFormDataFromUrlParams(
  params: UrlParamValues,
  currentFormData: CreateFormData
): Partial<CreateFormData> {
  const {
    prompt: promptParam,
    length: lengthParam,
    sleeve: sleeveParam,
    neckline: necklineParam,
    silhouette: silhouetteParam,
    fabric: fabricParam,
    preset: presetParam,
    color: colorParam,
  } = params;

  // Handle preset parameter
  let prompt = promptParam || "";
  let presetType: string | undefined = undefined;
  let isFrontUploadPreset = false;
  let isBackUploadPreset = false;
  let isUploadPreset = false;
  let isFrontCustomPreset = false;
  let isBackCustomPreset = false;
  let uploadedFrontImages = currentFormData.uploadedFrontImages || [];
  let uploadedBackImages = currentFormData.uploadedBackImages || [];

  if (presetParam) {
    if (presetParam === "upload") {
      // Upload preset: clear prompt, set upload flags, clear uploaded images, clear custom flags
      prompt = "";
      presetType = "Upload";
      isFrontUploadPreset = true;
      isUploadPreset = true;
      isFrontCustomPreset = false;
      isBackCustomPreset = false;
      uploadedFrontImages = [];
      uploadedBackImages = [];
    } else if (presetParam === "custom") {
      // Custom preset: clear prompt, clear all upload flags and images, set custom flags
      prompt = "";
      presetType = "Custom";
      isFrontUploadPreset = false;
      isBackUploadPreset = false;
      isUploadPreset = false;
      isFrontCustomPreset = true;
      isBackCustomPreset = false;
      uploadedFrontImages = [];
      uploadedBackImages = [];
    } else {
      // Regular preset: set prompt, clear all upload flags and images, clear custom flags
      const presetValue = getPresetByUrlParam(presetParam);
      if (presetValue !== null) {
        prompt = presetValue;
        // Find the preset label that matches the prompt value
        const matchingPreset = FRONT_PRESETS.find(p => p.value === presetValue);
        if (matchingPreset) {
          presetType = matchingPreset.label;
        }
      }
      isFrontUploadPreset = false;
      isBackUploadPreset = false;
      isUploadPreset = false;
      isFrontCustomPreset = false;
      isBackCustomPreset = false;
      uploadedFrontImages = [];
      uploadedBackImages = [];
    }
  }

  const result: Partial<CreateFormData> = {
    prompt,
    length: lengthParam || "",
    sleeve: sleeveParam || "",
    neckline: necklineParam || "",
    waistline: "", // Not provided in URL
    silhouette: silhouetteParam || "",
    fabric: fabricParam || "",
    color: colorParam || "",
    isFrontUploadPreset,
    isBackUploadPreset,
    isUploadPreset,
    isFrontCustomPreset,
    isBackCustomPreset,
    uploadedFrontImages,
    uploadedBackImages,
  };

  // Only set preset_type if we have a preset param (not undefined)
  if (presetType !== undefined) {
    result.preset_type = presetType;
  }

  return result;
}

/**
 * Checks if form data has changed from URL params
 */
export function hasFormDataChanges(
  currentFormData: CreateFormData,
  newFormData: Partial<CreateFormData>
): boolean {
  return (
    currentFormData.prompt !== newFormData.prompt ||
    currentFormData.length !== newFormData.length ||
    currentFormData.sleeve !== newFormData.sleeve ||
    currentFormData.neckline !== newFormData.neckline ||
    currentFormData.silhouette !== newFormData.silhouette ||
    currentFormData.fabric !== newFormData.fabric ||
    currentFormData.color !== newFormData.color ||
    currentFormData.isFrontUploadPreset !== newFormData.isFrontUploadPreset ||
    currentFormData.isBackUploadPreset !== newFormData.isBackUploadPreset ||
    currentFormData.isUploadPreset !== newFormData.isUploadPreset ||
    currentFormData.isFrontCustomPreset !== newFormData.isFrontCustomPreset ||
    currentFormData.isBackCustomPreset !== newFormData.isBackCustomPreset ||
    JSON.stringify(currentFormData.uploadedFrontImages || []) !==
      JSON.stringify(newFormData.uploadedFrontImages || []) ||
    JSON.stringify(currentFormData.uploadedBackImages || []) !==
      JSON.stringify(newFormData.uploadedBackImages || [])
  );
}

/**
 * Creates a stable string representation of URL params for change tracking
 */
export function createUrlParamsKey(params: UrlParamValues): string {
  return JSON.stringify({
    prompt: params.prompt,
    length: params.length,
    sleeve: params.sleeve,
    neckline: params.neckline,
    silhouette: params.silhouette,
    fabric: params.fabric,
    preset: params.preset,
    color: params.color,
  });
}
